import React, { FC, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  CONFIRM_PASSWORD_FORM_FIELDS, CONFIRM_PASSWORD_FORM_FIELDS_NAMES,
  getConfirmPasswordSchema,
  getFormattedConfirmPasswordFields,
  initialValues,
} from '../../constants/confirmAccount/confirm-account-form';
import { errorHandler } from '../../utils/newApplicationUtils';
import { BUTTON_TYPE, ButtonComponent } from '../../components/ButtonComponent';
import { Title } from '../../components/developers/page-components/Title';
import { ConfirmPasswordInput } from '../../components/comfirmPassword/ConfirmPasswordInput';
import { CONFIRM_PASSWORD_INPUTS } from '../../constants/confirmAccount/confirm-account-inputs';
import { sendSentryMessage } from '../../utils/sentry-helper';
import { changePassword } from '../../store/identyti/actions/confirm-password';
import * as Sentry from '@sentry/react';
import { ADMIN_GROUPS, MERCHANT_GROUPS, parseJwt, STATUSES } from '../login/Login';
import * as auth from '../../redux/AuthRedux';
import { removeFavoriteFiltersFromStorage } from '../../utils/filter-helpers';
import axios from 'axios';
import { merchantActionTypes } from '../../store/merchnts/action-types';
import {
  APPLICATION_VIEW_BASE_VIEW,
  APPLICATION_VIEW_STEPS,
} from '../../components/application-view/ApplicationViewAsideMenu';
import { useDispatch } from 'react-redux';
import { useMerchants } from '../../store/merchnts/merchants-context';
import { errorAlert } from '../../components/ToastMessage';
import { BASE_URL } from '../../../config/environmentConfig';
import { LoginLayout } from '../../components/login/LoginLayout';
import { toAbsoluteUrl } from '../../utils';
import { InputType } from '../../components/form/form-input-constants';
import { FormInput } from '../../components/merchants/FormInput';
import { RedirectToLogin } from '../../components/login/RedirectToLogin';

import './ConfirmAccount.scss';

export const ConfirmAccount: FC = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { search } = useLocation();
	const dispatch = useDispatch();
	const { dispatch: merchantDispatch } = useMerchants();
	const searchParams = new URLSearchParams(search);

	const userEmail = searchParams.get('email')?.replace(' ', '+');

	const [loading, setLoading] = useState(false);
	const [showPassword, setShowPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);
	const [showTemporaryPassword, setShowTemporaryPassword] = useState(false);

	const formik = useFormik({
		initialValues,
		validationSchema: getConfirmPasswordSchema(t),
		onSubmit: () => void 0
	});

	useEffect(() => {
		if (userEmail) {
			formik.setFieldValue(CONFIRM_PASSWORD_FORM_FIELDS.EMAIL, userEmail);
		}
	}, []);

	const confirmUpdatePassword = async (): Promise<void> => {
		formik.handleSubmit();
		if (formik.isValid && Object.keys(formik.touched).length > 0 ) {
			setLoading(true);
			try {
				const params = getFormattedConfirmPasswordFields(formik.values);
				const user = await changePassword(params);
				console.log('data', user);

				if (user?.id) {
					await handleSuccessLogin(user);
				}

			} catch (e) {
				handleErrorConfirmPassword(e);
			}
		}
	};

	const handleSuccessLogin = async (user: any): Promise<void> => {
		try {
			setLoading(true);

			const parsedToken = parseJwt(user?.id);
			const userGroups = parsedToken?.['cognito:groups'];
			const applicantId = parsedToken?.['custom:mid'];
			const username = parsedToken?.['cognito:username'];
			const isPiiMasked = parsedToken?.['custom:isPiiMasked'];

			Sentry.setTag('mid', applicantId);
			Sentry.setTag('username', username);

			// TODO check partners permissions
			if (userGroups?.includes(ADMIN_GROUPS.SUPER_ADMIN) ||
				userGroups?.includes(ADMIN_GROUPS.PARTNER_ADMIN) ||
				userGroups?.includes(ADMIN_GROUPS.INTEGRATION_PARTNER_ADMIN)) {
				const loginData = {
					accessToken: user?.accessToken,
					idToken: user?.id,
					userData: {
						userId: parsedToken?.['cognito:username'],
						email: parsedToken?.email,
						emailVerified: parsedToken?.email_verified,
						userGroups,
						isPiiMasked,
						loginDate: new Date(),
					}
				};

				dispatch(auth.actions.login(loginData));
				removeFavoriteFiltersFromStorage();
				localStorage.setItem('userEmail', userEmail);
				return navigate('/dashboard');

			} else if (userGroups?.includes(ADMIN_GROUPS.MERCHANT_ADMIN) && applicantId) {

				const STATUS_URL = `${BASE_URL.MERCHANTS}/onboarding/${applicantId}/status`;
				const result = await axios(STATUS_URL, { headers: { Authorization: `Bearer ${user?.id}` }});
				const status = result?.data?.statusName;
				merchantDispatch({ type: merchantActionTypes.FETCH_STATUS_SUCCESS, payload: status });

				if (status === STATUSES.CANCELLED) {
					setLoading(false);
					return navigate('/auth/login');
				}

				const loginData = {
					accessToken: user?.accessToken,
					idToken: user?.id,
					userData: {
						userId: parsedToken?.['cognito:username'],
						email: parsedToken?.email,
						emailVerified: parsedToken?.email_verified,
						userGroups,
						applicationStatus: status,
						isPiiMasked,
						loginDate: new Date(),
					}
				};

				if (!status && status !== STATUSES.DRAFT) {
					throw new Error((!status && status !== STATUSES.DRAFT) ?
						'Either your login or password is wrong.' :
						'Something went wrong. Please, try to login later');
				}

				setLoading(false);
				// setSubmitting(false);

				switch (status) {
					case STATUSES.DECLINED:
						return;
					case STATUSES.APPROVED:
						dispatch(auth.actions.login(loginData));
						removeFavoriteFiltersFromStorage();
						localStorage.setItem('userEmail', userEmail);
						return navigate('/dashboard');
					case STATUSES.DRAFT:
						localStorage.setItem('userEmail', parsedToken.email);
						dispatch(auth.actions.login(loginData));
						return navigate(`/${APPLICATION_VIEW_BASE_VIEW}/${APPLICATION_VIEW_STEPS.BUSINESS_DETAILS}`);
					case STATUSES.CANCELLED:
					case STATUSES.SUBMITTED:
					case STATUSES.IN_REVIEW:
						localStorage.setItem('userEmail', parsedToken.email);
						dispatch(auth.actions.login(loginData));
						return navigate(`/${APPLICATION_VIEW_BASE_VIEW}/${APPLICATION_VIEW_STEPS.SUMMARY}`);
				}
			} else if (userGroups?.includes(MERCHANT_GROUPS.MERCHANT_SUPPORT)
				|| userGroups?.includes(MERCHANT_GROUPS.MERCHANT_SUPPORT_ADMIN)
				|| userGroups?.includes(MERCHANT_GROUPS.MERCHANT_FINANCE)
				|| userGroups?.includes(MERCHANT_GROUPS.MERCHANT_DEVELOPER)) {
				const STATUS_URL = `${BASE_URL.IDENTITY}/users/status?username=${username}`;
				const result = await axios.post(STATUS_URL, null, { headers: { Authorization: `Bearer ${user?.id}` }});

				const loginData = {
					accessToken: user?.accessToken,
					idToken: user?.id,
					userData: {
						userId: parsedToken?.['cognito:username'],
						email: parsedToken?.email,
						emailVerified: parsedToken?.email_verified,
						userGroups,
						isPiiMasked,
						// applicationStatus: result?.data
						loginDate: new Date(),
					}
				};

				setLoading(false);
				// setSubmitting(false);

				switch (result?.data) {
					case 'CONFIRMED':
						dispatch(auth.actions.login(loginData));
						removeFavoriteFiltersFromStorage();
						localStorage.setItem('userEmail', userEmail);
						return navigate('/dashboard');
					case 'FORCE_CHANGE_PASSWORD':
						return navigate('/auth/forgot-password');
				}
			} else {
				errorAlert({
					title: 'Something went wrong. Our team was informed. Please, try to login later',
					duration: 0,
					closable: true
				});
				setLoading(false);
			}

		} catch (e) {
			handleErrorConfirmPassword(e);
		}
	};

	const handleErrorConfirmPassword = (e): void => {
		setLoading(false);
		sendSentryMessage({ name: `POST confirm password error`, context: e });
		console.log('handleErrorConfirmPassword error', e.response);
		const confirmPasswordError = Object.values(e.response?.data?.errors || {});
		// setSubmitting(false);
		if (e.response?.data?.['Captcha'] || e.response?.data?.errors?.General) {
			errorHandler({ e, formik });
			return;
		}
		if (!e?.response?.status && e.message) {
			errorAlert({
				title: e.message,
				duration: 0,
				closable: true
			});
		} else if (confirmPasswordError[0][0] === 'e_100' || confirmPasswordError[0][0] === 'e_101') {
			errorAlert({
				title: 'Account is blocked. Please, contact administrator.',
				duration: 0,
				closable: true
			});
		} else if (e?.response?.status === 403) {
			errorAlert({
				title: 'Something went wrong. Our team was informed. Please, try to login later',
				duration: 0,
				closable: true
			});
		} else {
			errorHandler({ e, formik });
		}
	};

  const INPUTS = CONFIRM_PASSWORD_INPUTS({
    t,
    showPassword, setShowPassword,
    showConfirmPassword, setShowConfirmPassword,
    showTemporaryPassword, setShowTemporaryPassword,
  });

  return (
    <LoginLayout>
      <div className="forgot-password-right-column position-relative">
        <div className="forgot-password-form-wrapper">
          <div className="forgot-password-app-logo mb-10">
            <img
              className="logo-medium"
              src={toAbsoluteUrl('/media/flex-logo-small.svg')}
              alt="Flex"
            />
          </div>

          <Title
            title={t('CONFIRM_PASSWORD.CONFIRM_ACCOUNT')}
            additionalClass="fs-1 fw-bolder text-center mb-10"
          />

          <form
            onSubmit={formik.handleSubmit}
            className="form confirm-password-form-container"
          >

            <FormInput
              formik={formik}
              fieldName={CONFIRM_PASSWORD_FORM_FIELDS.EMAIL}
              label={t(CONFIRM_PASSWORD_FORM_FIELDS_NAMES.EMAIL)}
              placeholder={t(CONFIRM_PASSWORD_FORM_FIELDS_NAMES.EMAIL)}
              inputType={InputType.Text}
              additionalClasses="col-12 col-sm-12 col-md-12 mb-10"
              autoComplete="email"
              required
            />

            {
              INPUTS.map(input => (
                <ConfirmPasswordInput
                  {...input}
                  formik={formik}
                  key={input.key}
                  required
                  isSmallInput
                />
              ))
            }

            <ButtonComponent
              buttonType={BUTTON_TYPE.DEFAULT_BLACK}
              buttonName={t('CONFIRM_PASSWORD.BUTTON_TEXT')}
              onButtonClick={confirmUpdatePassword}
              isDisabled={loading || !formik.isValid}
              loading={loading}
              type="submit"
              additionalClass="w-100 mt-5"
            />

	          <RedirectToLogin
		          additionalClass="mt-5 text-center fs-7"
		          hasAccount
	          />
          </form>
        </div>
      </div>
    </LoginLayout>

	);
};
